import { useEffect, useMemo, useRef } from 'react';
import type { BoundActions, HookActionsFunction, HookFunction, Store } from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { formatTapTraits, type TapTraitAttribute } from './formatTapTraits.ts';

export type SiteUserTraits = {
	attributes: TapTraitAttribute[] | null;
};

type SiteUserTraitsState = {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
};

export type UseSiteUserTraitsOptions = {
	/** When true, the hook is fully idle: no preloaded data, no store reads, no network fetch. Returns { data: null, loading: false, error: null }. Use for features that are entirely inactive */
	skip?: boolean;
	/** Legacy: blocks the network bootstrap fetch only. Preloaded data (SSR/window.__SSR_TAP_CONTEXT__) and store data may still be returned. Do not migrate existing callers without auditing each one. */
	skipBootstrap?: boolean;
	fetchPolicy?: 'cache-first' | 'cache-and-network';
};

export type UseSiteUserTraitsResult = {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
};

const initialState: SiteUserTraitsState = {
	data: null,
	loading: false,
	error: null,
};

const actions = {
	start:
		() =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }): void => {
			setState({ loading: true, error: null });
		},
	success:
		(data: SiteUserTraits) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }): void => {
			setState({ data, loading: false, error: null });
		},
	error:
		(err: Error) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }): void => {
			setState({ error: err, loading: false });
		},
};

export const siteUserTraitsStore: Store<
	SiteUserTraitsState,
	{
		start: () => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
		success: (
			data: SiteUserTraits,
		) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
		error: (
			err: Error,
		) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'siteUserTraitsStore',
});

export const useSiteUserTraitsStore: HookFunction<
	SiteUserTraitsState,
	BoundActions<
		SiteUserTraitsState,
		{
			start: () => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
			success: (
				data: SiteUserTraits,
			) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
			error: (
				err: Error,
			) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
		}
	>,
	void
> = createHook(siteUserTraitsStore);
export const useSiteUserTraitsActions: HookActionsFunction<
	BoundActions<
		SiteUserTraitsState,
		{
			start: () => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
			success: (
				data: SiteUserTraits,
			) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
			error: (
				err: Error,
			) => ({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => void;
		}
	>
> = createActionsHook(siteUserTraitsStore);

async function fetchSiteUserTraitsData(cloudId: string, userId: string): Promise<SiteUserTraits> {
	const res = await cfetch(
		`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`,
		{ method: 'GET' },
	);
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as SiteUserTraits;
}

// Use a module-level variable to ensure we only fetch once per app lifecycle
// This way multiple components using the hook won't trigger multiple fetches
let hasFetched: boolean = false;

// Test utility to reset the hasFetched flag
export function resetHasFetched(): void {
	hasFetched = false;
}

function getSiteUserTraitsFromSources(): SiteUserTraits | null {
	let attributes: TapTraitAttribute[] | null = null;
	// First check window.__SSR_TAP_CONTEXT__ which contains raw tapContext data
	if (typeof window !== 'undefined' && window.__SSR_TAP_CONTEXT__) {
		attributes = formatTapTraits(window.__SSR_TAP_CONTEXT__.siteUser);
		if (attributes) {
			return { attributes };
		}
	}

	// Fallback to getSSRRenderInputs() for SSR scenarios
	const { tapContext } = getSSRRenderInputs();
	attributes = formatTapTraits(tapContext?.siteUser);
	if (attributes) {
		return { attributes };
	}

	return null;
}

export function useSiteUserTraits({
	skip,
	skipBootstrap,
	fetchPolicy = 'cache-first',
}: UseSiteUserTraitsOptions = {}): UseSiteUserTraitsResult {
	// skip=true -> full idle, no preload/store/fetch
	const preloadedData = useMemo(() => (skip ? null : getSiteUserTraitsFromSources()), [skip]);

	const [storeState, storeActions] = useSiteUserTraitsStore();
	const { accessStatus, cloudId, userId } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	// Track whether cache-and-network fetch dispatched this mount
	const fetchDispatchedRef = useRef(false);

	useEffect(() => {
		if (skip) return;
		if (!cloudId || !userId) return;
		if (skipBootstrap) return;
		if (!hasLicensedAccess) return;

		if (fetchPolicy === 'cache-first') {
			// Old behavior: don't fetch if we have data from any source
			if (preloadedData || storeState.data || storeState.loading || storeState.error) return;
			if (hasFetched) return;

			hasFetched = true;
			storeActions.start();
			void fetchSiteUserTraitsData(cloudId, userId).then(
				(data) => storeActions.success(data),
				(err: Error) => storeActions.error(err),
			);
		} else {
			// cache-and-network: fetch once per mount
			if (storeState.loading) {
				fetchDispatchedRef.current = true;
				return;
			}
			if (fetchDispatchedRef.current) return;

			fetchDispatchedRef.current = true;
			storeActions.start();
			void fetchSiteUserTraitsData(cloudId, userId).then(
				(data) => storeActions.success(data),
				(err: Error) => storeActions.error(err),
			);
		}
	}, [
		skip,
		skipBootstrap,
		fetchPolicy,
		cloudId,
		userId,
		hasLicensedAccess,
		preloadedData,
		storeState,
		storeActions,
	]);

	if (skip) {
		return { data: null, loading: false, error: null };
	}

	// Fetched store result wins over preloaded data
	if (storeState.data) {
		return { data: storeState.data, loading: storeState.loading, error: storeState.error };
	}

	if (preloadedData) {
		return { data: preloadedData, loading: storeState.loading, error: null };
	}

	return { data: null, loading: storeState.loading, error: storeState.error };
}
